import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';

const NotificationManager = () => {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = () => {
    const reminderTime = localStorage.getItem('story-reminder-time');
    setIsSubscribed(!!reminderTime);
  };

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported in this browser');
      return;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        toast.success('Notifications enabled!');
        scheduleReminder();
      } else if (result === 'denied') {
        toast.error('Notification permission denied');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Failed to enable notifications');
    }
  };

  const scheduleReminder = () => {
    // Set default reminder time to 7:00 PM
    const now = new Date();
    const reminderTime = new Date();
    reminderTime.setHours(19, 0, 0, 0); // 7:00 PM

    // If it's past 7 PM today, schedule for tomorrow
    if (now > reminderTime) {
      reminderTime.setDate(reminderTime.getDate() + 1);
    }

    const timeUntilReminder = reminderTime.getTime() - now.getTime();

    // Store reminder time
    localStorage.setItem('story-reminder-time', reminderTime.toISOString());
    setIsSubscribed(true);

    // Schedule notification
    setTimeout(() => {
      sendNotification();
      // Reschedule for next day
      scheduleReminder();
    }, timeUntilReminder);

    toast.success('Daily story reminder set for 7:00 PM');
  };

  const sendNotification = () => {
    if (Notification.permission === 'granted') {
      const notification = new Notification('StoryTime Studio 📚', {
        body: '🌙 Time to create a magical bedtime story for your little one!',
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        tag: 'daily-reminder',
        requireInteraction: false,
        vibrate: [200, 100, 200],
      });

      notification.onclick = () => {
        window.focus();
        window.location.href = '/generate';
        notification.close();
      };
    }
  };

  const unsubscribe = () => {
    localStorage.removeItem('story-reminder-time');
    setIsSubscribed(false);
    toast.info('Daily reminders disabled');
  };

  const testNotification = () => {
    if (Notification.permission === 'granted') {
      sendNotification();
    } else {
      requestPermission();
    }
  };

  return (
    <div className="flex items-center gap-2">
      {permission === 'granted' ? (
        isSubscribed ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={unsubscribe}
            title="Disable daily reminders"
            data-testid="disable-notifications"
          >
            <Bell className="w-5 h-5 text-primary" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={scheduleReminder}
            title="Enable daily reminders"
            data-testid="enable-notifications"
          >
            <BellOff className="w-5 h-5" />
          </Button>
        )
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={requestPermission}
          title="Enable notifications"
          data-testid="request-notifications"
        >
          <BellOff className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
};

export default NotificationManager;
